import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export const useSortableTable = <T,>(data: T[], defaultSortKey: keyof T) => {
  const [sortKey, setSortKey] = useState<keyof T>(defaultSortKey);
  const [direction, setDirection] = useState<SortDirection>('asc');

  const sortBy = (key: keyof T) => {
    if (sortKey === key) {
      setDirection(direction === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setDirection('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!sortKey) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];

      if (aValue === bValue) return 0;

      const order = direction === 'asc' ? 1 : -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * order;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * order;
      }
      
      // Fallback for other types
      if (aValue > bValue) return order;
      if (aValue < bValue) return -order;
      return 0;
    });
  }, [data, sortKey, direction]);

  return { sorted, sortBy, sortKey, direction };
};